import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { edgesOf, grommetPoints, isGlossy, optionOn, roundedCornerInches, sizeLabel } from "@/lib/signs";

const inch = (n, ppi, min = 0) => Math.max(min, n * ppi);
const WALL = "#eef0f3";

/** A dimension callout: a hairline with the measurement in a pill at its middle. */
function Ruler({ label, vertical, color, testid }) {
    const line = <span className={`${vertical ? "w-px flex-1" : "h-px flex-1"} bg-zinc-400/70`} />;
    return (
        <div className={`absolute flex items-center gap-1.5 font-mono text-[11px] font-bold tracking-widest ${vertical ? "-left-9 top-0 h-full flex-col" : "-top-8 left-0 w-full"}`}>
            {line}
            <span className={`rounded-full border border-zinc-300 bg-white px-2 py-0.5 shadow-sm ${vertical ? "-rotate-90" : ""}`} style={{ color }} data-testid={testid}>{label}</span>
            {line}
        </div>
    );
}

/** A metal ring with the wall showing through its hole. */
function Grommet({ x, y, size }) {
    return (
        <span className="pointer-events-none absolute grid place-items-center rounded-full shadow-[0_1px_2px_rgba(0,0,0,0.45)]"
            style={{
                width: size, height: size, left: `calc(${x * 100}% - ${size / 2}px)`, top: `calc(${y * 100}% - ${size / 2}px)`,
                background: "radial-gradient(circle at 35% 30%, #ffffff 0%, #d7dbe0 28%, #8b929c 62%, #4b515a 100%)",
            }} data-testid="sign-grommet">
            <span className="rounded-full" style={{ width: size * 0.46, height: size * 0.46, background: WALL, boxShadow: "inset 0 1px 2px rgba(0,0,0,0.6)" }} />
        </span>
    );
}

/**
 * True-scale sign preview on a light wall with a drafting grid. The face keeps the real aspect
 * ratio and rescales live; every finishing option is drawn where it really goes.
 */
export default function SignCanvas({ product, width, height, artUrl, options = {}, transform, onTransform, doubleSided, theme, sideLabel, reserveBottom = 0, safeZoneIn = 0 }) {
    const boxRef = useRef(null);
    const [box, setBox] = useState({ w: 0, h: 0 });
    const drag = useRef(null);

    useLayoutEffect(() => {
        const el = boxRef.current;
        if (!el) return;
        const ro = new ResizeObserver(() => setBox({ w: el.clientWidth, h: el.clientHeight }));
        ro.observe(el);
        setBox({ w: el.clientWidth, h: el.clientHeight });
        return () => ro.disconnect();
    }, []);

    useEffect(() => {
        const move = (e) => {
            if (!drag.current || !onTransform) return;
            const p = e.touches ? e.touches[0] : e;
            onTransform({ ...transform, x: drag.current.ox + (p.clientX - drag.current.sx), y: drag.current.oy + (p.clientY - drag.current.sy) });
        };
        const up = () => { drag.current = null; };
        window.addEventListener("mousemove", move);
        window.addEventListener("mouseup", up);
        window.addEventListener("touchmove", move, { passive: false });
        window.addEventListener("touchend", up);
        return () => {
            window.removeEventListener("mousemove", move);
            window.removeEventListener("mouseup", up);
            window.removeEventListener("touchmove", move);
            window.removeEventListener("touchend", up);
        };
    }, [transform, onTransform]);

    const wIn = Number(width) || 0;
    const hIn = Number(height) || 0;
    const hasSize = wIn > 0 && hIn > 0;
    const pad = 96;
    const ppi = hasSize && box.w ? Math.min((box.w - pad) / wIn, (box.h - pad - reserveBottom) / hIn) : 0;
    const sw = Math.max(wIn * ppi, 0);
    const sh = Math.max(hIn * ppi, 0);
    const accent = theme?.ink || theme?.primary || "#0B0B0B";

    const radius = roundedCornerInches(options.rounded_corners) * ppi;
    const hem = options.welding === "yes" || options.welding === true;
    const pocketEdges = edgesOf(options.pole_pocket);
    const ropeEdges = edgesOf(options.rope);
    const slits = optionOn(options.wind_slits);
    const contour = optionOn(options.contour_cut) || options.custom_cut === "yes";
    const stakes = optionOn(options.step_stakes_standard) || optionOn(options.step_stakes_hd);
    const standoffs = optionOn(options.standoff_silver) || optionOn(options.standoff_black);
    const gloss = isGlossy(options) && !(sideLabel === "back" && options.coating === "gloss_front");
    const safePx = safeZoneIn > 0 ? inch(safeZoneIn, ppi, 4) : 0;
    const grommets = optionOn(options.grommets) ? grommetPoints(options.grommet_sides || "top_bottom", options.grommet_spacing, wIn, hIn) : [];
    const grommetPx = inch(0.9, ppi, 10);
    const pocketPx = inch(3, ppi, 10);
    const ropePx = inch(0.35, ppi, 4);
    const hemPx = inch(1, ppi, 3);

    const startDrag = (e) => {
        if (!artUrl || !onTransform) return;
        const p = e.touches ? e.touches[0] : e;
        drag.current = { sx: p.clientX, sy: p.clientY, ox: transform.x, oy: transform.y };
    };

    const edgeBox = (edge, thick) => ({
        top: { left: 0, top: 0, width: "100%", height: thick },
        bottom: { left: 0, bottom: 0, width: "100%", height: thick },
        left: { left: 0, top: 0, height: "100%", width: thick },
        right: { right: 0, top: 0, height: "100%", width: thick },
    }[edge]);

    const slitCols = Math.max(2, Math.round(wIn / 18));
    const slitRows = Math.max(1, Math.round(hIn / 18));

    return (
        <div ref={boxRef} className="relative flex h-full w-full items-center justify-center overflow-hidden" style={{ background: WALL }} data-testid="sign-canvas">
            <div className="pointer-events-none absolute inset-0" style={{
                backgroundImage: "linear-gradient(rgba(15,23,42,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,0.06) 1px, transparent 1px), linear-gradient(rgba(15,23,42,0.11) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,0.11) 1px, transparent 1px)",
                backgroundSize: "24px 24px, 24px 24px, 120px 120px, 120px 120px",
            }} />

            {!hasSize && (
                <p className="relative z-10 max-w-xs text-center text-sm text-zinc-500" data-testid="sign-canvas-empty">
                    Upload an image or set a size. Your sign draws itself here at true scale.
                </p>
            )}

            {hasSize && safePx > 0 && (
                <span className="absolute bottom-4 right-4 z-10 rounded-full border border-sky-200 bg-white/90 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-sky-700 shadow-sm" data-testid="safe-zone-note">
                    Dashed line = safe zone · keep text and logos inside it
                </span>
            )}

            {hasSize && (
                <div className="relative" style={{ width: sw, height: sh, marginBottom: reserveBottom }}>
                    <Ruler label={sizeLabel(wIn, 0).split(" ×")[0]} color={accent} testid="ruler-width" />
                    <Ruler label={sizeLabel(hIn, 0).split(" ×")[0]} color={accent} vertical testid="ruler-height" />

                    <div className="relative h-full w-full overflow-hidden bg-white shadow-[0_30px_60px_-24px_rgba(15,23,42,0.45)] ring-1 ring-black/10"
                        style={{ borderRadius: radius }} data-testid="sign-face">
                        {artUrl ? (
                            <img src={artUrl} alt="Your artwork" draggable={false} onMouseDown={startDrag} onTouchStart={startDrag}
                                className="absolute left-1/2 top-1/2 max-w-none cursor-move select-none"
                                style={{ transform: `translate(-50%,-50%) translate(${transform.x}px, ${transform.y}px) scale(${transform.zoom})`, width: sw, height: sh, objectFit: transform.fit || "cover" }}
                                data-testid="sign-artwork" />
                        ) : (
                            <div className="flex h-full w-full items-center justify-center px-4 text-center font-mono text-[11px] uppercase tracking-widest text-zinc-400">
                                {sideLabel ? `${sideLabel} image goes here` : "Your image goes here"}
                            </div>
                        )}

                        {gloss && <div className="pointer-events-none absolute inset-0" style={{ background: "linear-gradient(115deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0) 38%, rgba(255,255,255,0) 62%, rgba(255,255,255,0.25) 100%)" }} />}

                        {safePx > 0 && <div className="pointer-events-none absolute border border-dashed border-sky-500/80" style={{ inset: safePx }} data-testid="sign-safe-zone" />}

                        {options.flute_direction && (
                            <div className="pointer-events-none absolute inset-0 opacity-[0.12]" style={{
                                backgroundImage: `repeating-linear-gradient(${options.flute_direction === "vertical" ? "90deg" : "0deg"}, rgba(0,0,0,0.6) 0 1px, transparent 1px ${Math.max(6, 0.25 * ppi)}px)`,
                            }} />
                        )}

                        {hem && <div className="pointer-events-none absolute border border-dashed border-black/40" style={{ inset: hemPx, borderRadius: radius }} data-testid="sign-hem" />}

                        {pocketEdges.map((edge) => (
                            <div key={edge} className={`pointer-events-none absolute bg-black/[0.09] ${{ top: "border-b", bottom: "border-t", left: "border-r", right: "border-l" }[edge]} border-dashed border-black/50`}
                                style={edgeBox(edge, pocketPx)} data-testid={`sign-pole-pocket-${edge}`} />
                        ))}

                        {ropeEdges.map((edge) => (
                            <div key={edge} className="pointer-events-none absolute rounded-full shadow-sm"
                                style={{
                                    left: hemPx * 0.6, right: hemPx * 0.6, height: ropePx, [edge]: hemPx * 0.55,
                                    background: "repeating-linear-gradient(60deg, #c9a97a 0 3px, #8f6b3f 3px 6px)",
                                }} data-testid={`sign-rope-${edge}`} />
                        ))}

                        {slits && (
                            <div className="pointer-events-none absolute inset-0" data-testid="sign-wind-slits">
                                {Array.from({ length: slitRows }).flatMap((_, r) => Array.from({ length: slitCols }).map((__, c) => (
                                    <span key={`${r}-${c}`} className="absolute border-2 border-t-0 border-black/50"
                                        style={{
                                            width: inch(3, ppi, 14), height: inch(1.5, ppi, 7),
                                            left: `calc(${((c + 0.5) / slitCols) * 100}% - ${inch(1.5, ppi, 7)}px)`,
                                            top: `calc(${((r + 0.5) / slitRows) * 100}% - ${inch(0.75, ppi, 3)}px)`,
                                            borderRadius: `0 0 ${inch(3, ppi, 14)}px ${inch(3, ppi, 14)}px`,
                                        }} />
                                )))}
                            </div>
                        )}

                        {standoffs && [[0, 0], [1, 0], [0, 1], [1, 1]].map(([x, y], i) => (
                            <span key={i} className="pointer-events-none absolute rounded-full shadow"
                                style={{
                                    width: grommetPx * 2.2, height: grommetPx * 2.2,
                                    left: `calc(${x * 100}% - ${grommetPx * 1.1}px)`, top: `calc(${y * 100}% - ${grommetPx * 1.1}px)`,
                                    transform: `translate(${x ? -grommetPx * 1.6 : grommetPx * 1.6}px, ${y ? -grommetPx * 1.6 : grommetPx * 1.6}px)`,
                                    background: optionOn(options.standoff_black) ? "linear-gradient(145deg,#4b4b4b,#111)" : "linear-gradient(145deg,#e9e9ee,#8f97a3)",
                                }} data-testid="sign-standoff" />
                        ))}

                        {grommets.map((g, i) => <Grommet key={i} x={g.x} y={g.y} size={grommetPx} />)}
                    </div>

                    {contour && <div className="pointer-events-none absolute border-2 border-dashed border-fuchsia-500/80" style={{ inset: -Math.max(4, 0.5 * ppi), borderRadius: radius + 6 }} data-testid="sign-contour" />}

                    {stakes && (
                        <div className="pointer-events-none absolute left-1/2 top-full flex -translate-x-1/2 gap-10" data-testid="sign-stakes">
                            {[0, 1].map((i) => <span key={i} className="block w-[3px] bg-gradient-to-b from-zinc-400 to-zinc-700" style={{ height: Math.max(24, 10 * ppi) }} />)}
                        </div>
                    )}

                    {doubleSided && (
                        <span className="absolute -bottom-8 right-0 rounded-full border bg-white px-3 py-1 font-mono text-[11px] uppercase tracking-widest shadow-sm"
                            style={{ color: accent, borderColor: accent }} data-testid="sign-double-sided">
                            Double-sided{sideLabel ? ` · ${sideLabel}` : ""}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}
